import type { ProductAnalyticsEvent } from "./eventTypes";

export const OUTBOX_EXPIRY_MS = 24 * 60 * 60 * 1_000;
const DEFAULT_LEASE_MS = 30_000;
const DB_NAME = "pinpoint-product-analytics-v1";
const STORE_NAME = "events";

export type OutboxRecord = {
  id: string;
  event: ProductAnalyticsEvent;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  leaseOwner?: string;
  leaseUntil?: number;
};

export interface EventStore {
  put(record: OutboxRecord): Promise<void>;
  claimDue(now: number, owner: string, leaseMs: number): Promise<OutboxRecord | null>;
  remove(id: string, owner?: string): Promise<void>;
  retry(id: string, owner: string, nextAttemptAt: number): Promise<void>;
  list(): Promise<OutboxRecord[]>;
}

export class InMemoryEventStore implements EventStore {
  private readonly records = new Map<string, OutboxRecord>();

  async put(record: OutboxRecord) {
    const existing = this.records.get(record.id);
    if (existing && JSON.stringify(existing.event) !== JSON.stringify(record.event)) throw new Error("Analytics clientEventId collision");
    if (!existing) this.records.set(record.id, structuredClone(record));
  }
  async claimDue(now: number, owner: string, leaseMs: number) {
    const record = [...this.records.values()]
      .sort((a, b) => a.createdAt - b.createdAt)
      .find((item) => item.nextAttemptAt <= now && (!item.leaseUntil || item.leaseUntil <= now));
    if (!record) return null;
    record.leaseOwner = owner;
    record.leaseUntil = now + leaseMs;
    this.records.set(record.id, record);
    return structuredClone(record);
  }
  async remove(id: string, owner?: string) {
    const record = this.records.get(id);
    if (record && (!owner || record.leaseOwner === owner)) this.records.delete(id);
  }
  async retry(id: string, owner: string, nextAttemptAt: number) {
    const record = this.records.get(id);
    if (!record || record.leaseOwner !== owner) return;
    record.attempts += 1;
    record.nextAttemptAt = nextAttemptAt;
    delete record.leaseOwner;
    delete record.leaseUntil;
  }
  async list() { return [...this.records.values()].map((record) => structuredClone(record)); }
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });
}

export class IndexedDbEventStore implements EventStore {
  private readonly database: Promise<IDBDatabase>;

  constructor(indexedDb: IDBFactory = window.indexedDB) {
    this.database = new Promise((resolve, reject) => {
      const request = indexedDb.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(record: OutboxRecord) {
    const db = await this.database;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    const existing = await requestResult(store.get(record.id) as IDBRequest<OutboxRecord | undefined>);
    if (existing && JSON.stringify(existing.event) !== JSON.stringify(record.event)) {
      transaction.abort();
      try { await done; } catch { /* collision is the public diagnostic */ }
      throw new Error("Analytics clientEventId collision");
    }
    if (!existing) store.add(record);
    await done;
  }

  async claimDue(now: number, owner: string, leaseMs: number) {
    const db = await this.database;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    const records = await requestResult(store.getAll() as IDBRequest<OutboxRecord[]>);
    const record = records.sort((a, b) => a.createdAt - b.createdAt)
      .find((item) => item.nextAttemptAt <= now && (!item.leaseUntil || item.leaseUntil <= now));
    if (record) {
      record.leaseOwner = owner;
      record.leaseUntil = now + leaseMs;
      store.put(record);
    }
    await done;
    return record ?? null;
  }

  async remove(id: string, owner?: string) {
    const db = await this.database;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    const record = await requestResult(store.get(id) as IDBRequest<OutboxRecord | undefined>);
    if (record && (!owner || record.leaseOwner === owner)) store.delete(id);
    await done;
  }

  async retry(id: string, owner: string, nextAttemptAt: number) {
    const db = await this.database;
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    const record = await requestResult(store.get(id) as IDBRequest<OutboxRecord | undefined>);
    if (record?.leaseOwner === owner) {
      record.attempts += 1;
      record.nextAttemptAt = nextAttemptAt;
      delete record.leaseOwner;
      delete record.leaseUntil;
      store.put(record);
    }
    await done;
  }

  async list() {
    const db = await this.database;
    const transaction = db.transaction(STORE_NAME, "readonly");
    return requestResult(transaction.objectStore(STORE_NAME).getAll() as IDBRequest<OutboxRecord[]>);
  }
}

export type DeliveryResult =
  | { kind: "ack"; clientEventId: string; eventId?: string }
  | { kind: "terminal" };
export interface EventTransport { send(event: ProductAnalyticsEvent): Promise<DeliveryResult>; }

type OutboxOptions = { ownerId?: string; baseBackoffMs?: number; leaseMs?: number };

export class EventOutbox {
  private readonly ownerId: string;
  private readonly baseBackoffMs: number;
  private readonly leaseMs: number;

  constructor(private readonly store: EventStore, private readonly transport: EventTransport, options: OutboxOptions = {}) {
    this.ownerId = options.ownerId ?? crypto.randomUUID();
    this.baseBackoffMs = options.baseBackoffMs ?? 1_000;
    this.leaseMs = options.leaseMs ?? DEFAULT_LEASE_MS;
  }

  async enqueue(event: ProductAnalyticsEvent, now = Date.now()) {
    await this.store.put({ id: event.clientEventId, event, createdAt: now, attempts: 0, nextAttemptAt: now });
  }

  async flush(now = Date.now()): Promise<void> {
    while (true) {
      const record = await this.store.claimDue(now, this.ownerId, this.leaseMs);
      if (!record) return;
      if (now - record.createdAt > OUTBOX_EXPIRY_MS) {
        await this.store.remove(record.id, this.ownerId);
        continue;
      }
      try {
        const result = await this.transport.send(record.event);
        if (result.kind === "terminal" || result.clientEventId === record.id) {
          await this.store.remove(record.id, this.ownerId);
          continue;
        }
      } catch {
        // A transport failure is retryable; the original persisted event and ID remain authoritative.
      }
      const delay = Math.min(this.baseBackoffMs * 2 ** record.attempts, 60_000);
      await this.store.retry(record.id, this.ownerId, now + delay);
    }
  }
}
